import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { MapView } from "@/components/MapView";
import { useGeolocation } from "@/hooks/useGeolocation";
import { useWakeLock } from "@/hooks/useWakeLock";
import { LocateFixed } from "lucide-react";

type ThemeMode = "light" | "dark";

type AppConfig = { stadiaApiKey: string };

export default function App() {
  const { geo, error, permissionState } = useGeolocation();
  const [theme, setTheme] = useState<ThemeMode>(() => getInitialTheme());
  const [_config, setConfig] = useState<AppConfig | null>(null);
  useWakeLock(true);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    document.documentElement.style.colorScheme = theme;
    window.localStorage.setItem("taze-theme", theme);
  }, [theme]);

  useEffect(() => {
    fetch("/api/config")
      .then((r) => r.json() as Promise<AppConfig>)
      .then(setConfig)
      .catch(() => setConfig({ stadiaApiKey: "" }));
  }, []);

  if (!geo) {
    return (
      <main className="flex h-full w-full flex-col items-center justify-center gap-6 p-8 text-center">
        <LocateFixed className="size-12 text-primary" />
        <div>
          <h1 className="text-3xl font-semibold">Taze</h1>
          <p className="text-muted-foreground mt-2">
            {error
              ? error
              : permissionState === "denied"
                ? "Location permission was denied. Enable location in your Tesla browser to continue."
                : "Waiting for your first GPS fix…"}
          </p>
        </div>
        {error && (
          <Button onClick={() => window.location.reload()} size="lg">
            Try again
          </Button>
        )}
      </main>
    );
  }

  return (
    <main className="relative h-full w-full overflow-hidden bg-background">
      <MapView
        geo={geo}
        // stadiaApiKey={config?.stadiaApiKey ?? ""}
        stadiaApiKey=""
        theme={theme}
        onToggleTheme={() =>
          setTheme((current) => (current === "dark" ? "light" : "dark"))
        }
        onOpenWaze={() => openWazeLiveMap(geo)}
      />
      {/* {!config?.stadiaApiKey && (
        <div className="pointer-events-none absolute left-1/2 top-4 -translate-x-1/2 glass rounded-full border border-border px-4 py-2 text-sm text-muted-foreground">
          <Globe className="mr-2 inline size-4" />
          Add STADIA_API_KEY for vector tiles
        </div>
      )} */}
    </main>
  );
}

function openWazeLiveMap(geo: { lat: number; lon: number }) {
  const url = new URL("https://www.waze.com/live-map/directions");
  url.searchParams.set("to", `ll.${geo.lat},${geo.lon}`);
  url.searchParams.set("zoom", "15");
  url.searchParams.set("utm_source", "waze_website");
  window.open(url.toString(), "_blank", "noopener,noreferrer");
}

function getInitialTheme(): ThemeMode {
  const saved = window.localStorage.getItem("taze-theme");
  if (saved === "light" || saved === "dark") return saved;

  return window.matchMedia("(prefers-color-scheme: light)").matches
    ? "light"
    : "dark";
}
